import { ElementRef, inject } from "@angular/core";
import { Routes } from "@angular/router";
import { TreeNode } from "@openng/optimus-ui/api";
import * as FileSaver from 'file-saver';
import { ExcelColumn } from "src/modules/enaio/netgen/ExcelColumn";
import { CanDeactivateGuard } from "src/modules/sm-base/misc/can-deactivate.guard";
import { Strings } from "src/modules/utils/shared/strings";
import { Arrays } from "../shared/arrays";
import { Map2 } from "../shared/map2.model";
import { Objects } from "../shared/objects";
import { Utils } from "../shared/utils";
import { TreeNode2 } from "./tree-node2.model";
import { OrdinaryObject } from "../shared/ordinary-object.model";
import { colord } from "colord";
import { LocatorService } from "src/modules/sm-base/services/locator.service";
import { DomSanitizer, SafeHtml } from "@angular/platform-browser";
import { exec } from "child_process";
import { MainAppService } from "src/modules/app-template/services/main-app.service";

export class GuiUtilsInternal {

    //Erweiterungsmethoden, die vom angeschlossenen Backend abhängen und modulabhängig zu füllen sind
    static convertCsvToExcelFunction: (csv: string, columns: ExcelColumn[]) => Promise<string>;
    static openDialogFunction: (fileName?: string, selectFolder?: boolean) => Promise<string>;
    static saveDialogFunction: (fileName?: string, selectFolder?: boolean) => Promise<string>;
    static openMultipleDialogFunction: (fileName?: string, selectFolder?: boolean) => Promise<string[]>;

    async convertCsvToExcel(csv: string, columns: ExcelColumn[]): Promise<string> {
        return GuiUtilsInternal.convertCsvToExcelFunction(csv, columns);
    }

    async openDialog(fileName?: string, selectFolder?: boolean): Promise<string> {
        return GuiUtilsInternal.openDialogFunction(fileName, selectFolder);
    }

    async saveDialog(fileName?: string, selectFolder?: boolean): Promise<string> {
        return GuiUtilsInternal.saveDialogFunction(fileName, selectFolder);
    }

    async openMultipleDialog(fileName?: string, selectFolder?: boolean): Promise<string[]> {
        return GuiUtilsInternal.openMultipleDialogFunction(fileName, selectFolder);
    }

    private usedSingletonNames = new Set<string>();

    //Das ist ein Timer, um Dinge durchzuführen, nachdem die nächste ChangeDetection gelaufen ist.
    angularTimer(onTimer: () => void): any {
        return Utils.setTimerOnce(1, onTimer);
    }

    angularTimerDefer2(onTimer: () => void): any {
        return Utils.setTimerOnce(1, () => this.angularTimer(() => onTimer()));
    }

    angularTimerWithRetry(onTimer: () => void, canExecute: () => boolean, interval = 100, maxRetries = 10, executeImmediately = false, shouldAbort?: () => boolean): any {
        let retries = 0;

        const tryExecute = (): void => {
            if (shouldAbort?.()) {
                return;
            }
            if (canExecute()) {
                onTimer();
            }
            else if (retries < maxRetries) {
                retries++;
                Utils.setTimerOnce(interval, tryExecute);
            }
        };

        if (shouldAbort?.()) {
            return;
        }
        if (executeImmediately && canExecute()) {
            onTimer();
        }
        else {
            Utils.setTimerOnce(interval, tryExecute);
        }
    }

    async angularWaitForCd(): Promise<void> {
        await new Promise<void>(resolve => {
            Utils.setTimerOnce(0, () => resolve());
        });
    }

    base64ToArrayBuffer(base64: string): ArrayBuffer {
        let binary_string = window.atob(base64);
        let len = binary_string.length;
        let bytes = new Uint8Array(len);
        for (let i = 0; i < len; i++) {
            bytes[i] = binary_string.charCodeAt(i);
        }
        return bytes.buffer;
    }

    base64ToBlob(base64: string, contentType = '', sliceSize = 512): Blob {
        const byteCharacters = atob(base64);
        const byteArrays = [];
    
        for (let offset = 0; offset < byteCharacters.length; offset += sliceSize) {
            const slice = byteCharacters.slice(offset, offset + sliceSize);
    
            const byteNumbers = new Array(slice.length);
            for (let i = 0; i < slice.length; i++) {
                byteNumbers[i] = slice.charCodeAt(i);
            }
    
            const byteArray = new Uint8Array(byteNumbers);
            byteArrays.push(byteArray);
        }
    
        return new Blob(byteArrays, { type: contentType });
    }    

    buildColorScale(base: string): OrdinaryObject {
        return {
            50: colord(base).lighten(0.45).toHex(),
            100: colord(base).lighten(0.35).toHex(),
            200: colord(base).lighten(0.25).toHex(),
            300: colord(base).lighten(0.15).toHex(),
            400: colord(base).lighten(0.07).toHex(),
            500: colord(base).toHex(),            
            600: colord(base).darken(0.07).toHex(),
            700: colord(base).darken(0.15).toHex(),
            800: colord(base).darken(0.25).toHex(),
            900: colord(base).darken(0.35).toHex(),
            950: colord(base).darken(0.45).toHex()
        };
    }

    bytesToStringAscii(bytes: ArrayBuffer): string {
        return new TextDecoder('ascii').decode(bytes);
    }

    checkSingleton(o: object): boolean {
        let name = Objects.getTypeName(o);
        if (this.usedSingletonNames.has(name)) {
            throw new Error("Der Singleton " + name + " wurde bereits einmal registriert. Bitte prüfen, dass er nicht in der 'providers' Sektion des Moduls aufgeführt wird.");
        }
        else {
            this.usedSingletonNames.add(name);
            return true;
        }
    }

    colorStringToRgba(col: string): number {
        let canvas = document.createElement('canvas');
        canvas.width = 1;
        canvas.height = 1;
        let ctx = canvas.getContext('2d');

        ctx.clearRect(0, 0, 1, 1);
        ctx.fillStyle = '#000';
        ctx.fillStyle = col;
        let computed = ctx.fillStyle;
        ctx.fillStyle = '#fff';
        ctx.fillStyle = col;
        if (computed !== ctx.fillStyle) {
            return null;
        }
        ctx.fillRect(0, 0, 1, 1);
        let arr = [...ctx.getImageData(0, 0, 1, 1).data];

        return arr.length == 4 ? arr[0] << 24 | arr[1] << 16 | arr[2] << 8 | arr[3] : null;
    }

    isClipboardReadSupported(): boolean {
        return window.isSecureContext && navigator.clipboard?.readText != null && !navigator.userAgent.includes("Firefox/");
    }

    isClipboardWriteSupported(): boolean {
        return navigator.clipboard?.writeText != null;
    }

    //Bitte im Normalfall MainAppService.copyToClipboardOrDialog nutzen
    async copyToClipboard(text: string, successText: string = null): Promise<boolean> {
        if (!this.isClipboardWriteSupported()) {
            return false;
        }
        try {
            await navigator.clipboard.writeText(text);
            if (!Utils.isNoe(successText)) {
                inject(MainAppService).showSuccessToast(successText);
            }
            return true;
        }        
        catch (error) {
            console.log("Fehler beim Kopieren in die Zwischenablage:", error);
            return false;
        }
    }

    async fromClipboard(): Promise<string> {
        return navigator.clipboard.readText();
    }

    downloadBytesAsFile(arr: ArrayBuffer, fileName = "document.pdf"): void {
        let byteArray = new Uint8Array(arr);
        let a = window.document.createElement('a');

        a.href = window.URL.createObjectURL(new Blob([byteArray], { type: 'application/octet-stream' }));
        a.download = fileName;

        // Append anchor to body.
        document.body.appendChild(a);
        a.click();
        // Remove anchor from body
        document.body.removeChild(a);
    }

    flattenTree(nodes: TreeNode2[]): TreeNode2[] {
        let result = [...nodes];
        for (let node of nodes) {
            if (!Utils.isNoe(node.children)) {
                result = [...result, ...this.flattenTree(node.children)];
            }
        }
        return result;
    }

    generateTree<T>(items: T[], toTreeConverter: (item: T) => any[], toNodeConverter: (item: T, key: any[], index: number) => TreeNode2, sort = true, nodeMatcher?: (key: any, node: TreeNode2) => boolean): TreeNode2[] {
        let result: TreeNode2[] = [];
        for (let item of items) {
            let keys = toTreeConverter(item);
            let currentChildren = result;
            let index = 0;
            for (let key of keys) {
                let node = Arrays.getOrAdd(currentChildren, tn => nodeMatcher != null ? nodeMatcher(key, tn) : tn.label == key, () => toNodeConverter(item, keys, index));
                if (node.children == null && index != keys.length - 1) {
                    node.children = [];
                }
                currentChildren = node.children;
                index++;
            }
        }
        if (sort) {
            result = this.sortTreeNodes(result);
        }
        return result;
    }

    generateTree2<T>(items: T[], sort: boolean, expandAll: boolean, toTreeConverter: 
        (item: T, g: (key: string, gg: () => TreeNode2) => [string, () => TreeNode2], m: (label: string, data?: any, additional?: Partial<TreeNode2<T>>) => TreeNode2) => [string, () => TreeNode2][]): TreeNode2[] {
        let keys = new Map2<string, TreeNode2>();
        let result: TreeNode2[] = [];
        for (let item of items) {
            let nodeInfos = Arrays.withoutNull(toTreeConverter(item, (key, gg) => TreeNode2.g(key, gg), (label, data, additional) => TreeNode2.m(label, data, additional)));

            let currentChildren = result;
            let index = 0;
            for (let nodeInfo of nodeInfos) {
                let node = keys.get(nodeInfo[0]);    
                if (node == null) {
                    node = nodeInfo[1]();
                    node.key = nodeInfo[0];
                    keys.set(nodeInfo[0], node);
                    currentChildren.push(node);
                }
                if (node.children == null && index != nodeInfos.length - 1) {
                    node.children = [];
                    if (expandAll) {
                        node.expanded = true;
                    }
                }
                currentChildren = node.children;
                index++;
            }
        }
        if (sort) {
            result = this.sortTreeNodes(result);
        }
        return result;
    }

    getCurrentHostName(): string {
        return window.location.hostname;
    }

    getHtmlElementDebugString(e: HTMLElement, lastTitle: string = null, ignoreNodeText: string = null): string[] {
        let text = e.innerText;
        if (text != null) {
            text = Strings.crop(Strings.replaceAll(text, "[\\r\\n]", " "), 50, true).padEnd(50, ' ');
        }
        let realText = text;
        if (text == lastTitle) {
            text = "--\"--".padEnd(50, ' ');
        }
        let className = e.className == null || !Strings.is(e.className) ? "" : Strings.replaceAll(Strings.replaceAll(e.className, "ng-star-inserted|ng-untouched|ng-pristine|ng-valid", ""), "  ", "").trim();
        let nodeText = e.nodeName + (Utils.isNoe(className) ? "" : "." + className);
        let int = ignoreNodeText == null ? "(HTML|BODY|ROOT-COMPONENT)(\\..+)?" : ignoreNodeText;
        let result: string = null;
        if (Utils.isNoe(int) || !Strings.regexMatches(nodeText, "^" + int + "$")) {
            result = (!Utils.isNoe(text) ? "[" + text + "] " : "") + nodeText;
        }
        if (e.parentElement != null) {
            let nextStrings = this.getHtmlElementDebugString(e.parentElement, realText, ignoreNodeText);
            return result != null ? [result, ...nextStrings] : nextStrings;
            }
        else {
            return [result];
        }
    }

    getTreeItemCssStyle(color: number | string): string {
        let bc = Utils.toColorHexString(color);
        let text = Utils.getTextColor(bc);

        let hoverMix = text == "#000000" ? 'black 25%' : 'white 25%';
        return "background: " + bc + 
            "; --p-tree-node-color: " + Utils.getTextColor(bc) + 
            "; --p-tree-node-hover-color: " + Utils.getTextColor(bc) + 
            "; --p-tree-node-hover-background: color-mix(in srgb, " + bc + " 85%, " + hoverMix + ")";
   }

    getTrustedHtml(s: string): SafeHtml {
        return LocatorService.injector.get(DomSanitizer).bypassSecurityTrustHtml(s);
    }

    updateTreeSelectedColors(nodes: TreeNode[], selectedNodes: TreeNode[], element: HTMLElement): void {
        let selectedKeys = new Set<string>(selectedNodes.map(node => node.key));
        for (let node of this.flattenTree(nodes)) {
            if (node.data?.color != null) {
                let style = selectedKeys.has(node.key) ? this.getTreeItemCssStyle(node.data.color as number) : null;
                if (style != node.style) {
                    node.style = style;
                    if (element != null) {
                        const treeNode = element.querySelector("li.p-tree-node[data-id=\"" + node.key + "\"]");
                        if (treeNode != null) {
                            if (style != null) {
                                treeNode.setAttribute("style", style);
                            }
                            else {
                                treeNode.removeAttribute("style");
                            }
                        }
                    }
                }
            }
        }
    }

    htmlToElement(html: string): Node {
        let template = document.createElement('template');
        template.innerHTML = html.trim();
        return template.content.firstChild;
    }

    isComponentVisible(elementRef: ElementRef): boolean {
        return elementRef.nativeElement?.offsetParent != null;
    }

    objectToTree(o: any, sort = true, addReferences = false): TreeNode2[] {
        let result: TreeNode2[] = [];
        if (Arrays.is(o)) {
            let arr = o as any[];
            for (let i = 0; i < arr.length; i++) {
                let node: TreeNode2 = {label: "[" + i + "]", data: {key: i, value: Objects.is(arr[i]) ? "" : Objects.toString(arr[i]), ref: addReferences ? arr[i] : null}, sortText: Objects.toString(i).padStart(10, '0')};
                result.push(node);
                if (Objects.is(arr[i])) {
                    node.children = this.objectToTree(arr[i], sort, addReferences);
                }
            }
        }
        else {
            for (let key of Objects.getPropertyNames(o)) {
                let node: TreeNode2 = {label: key, data: {key, value: Objects.is(o[key]) ? "" : Objects.toString(o[key]), ref: addReferences ? o[key] : null}};
                result.push(node);
                if (Objects.is(o[key])) {
                    node.children = this.objectToTree(o[key], sort, addReferences);
                }
            }
        }
        if (sort) {
            result = this.sortTreeNodes(result);
        }
        return result;
    }

    saveFileAs(blob: Blob, fileName: string): void {
        FileSaver.saveAs(blob, fileName, { autoBom: false });
    }

    selectAll(event: any): void {
        event.target.select();
    }

    sortTreeNodes(nodes: TreeNode2[]): TreeNode2[] {
        let result = Arrays.sortBy(nodes, tn => (tn.sortText ?? "") + tn.label);
        for (let child of nodes) {
            if (child.children != null) {
                child.children = this.sortTreeNodes(child.children as TreeNode2[]);
            }
        }
        return result;
    }

    stringAsciiToArrayBuffer(s: string): ArrayBuffer {
        let len = s.length;
        let bytes = new Uint8Array(len);
        for (let i = 0; i < len; i++) {
            bytes[i] = s.charCodeAt(i);
        }
        return bytes.buffer;
    }

    updateRoutes(routes: Routes): Routes {
        for (let route of routes) {
            if (route.canDeactivate == null) {
                route.canDeactivate = [CanDeactivateGuard];
            }
        }

        return routes;
    }

}

export let GuiUtils = new GuiUtilsInternal();
